// src/components/common/Login.js - Popup login/signup + single-click logout
import React, { useContext, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import { BsPersonCircle, BsPersonBadge } from "react-icons/bs";
import { AuthContext } from "../Context/AuthContext";
import EmailLogin from "./EmailLogin";
import "../../Style/user.css";

const Login = () => {
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const {
    user,
    userType,
    isLoggedIn,
    logout,
    getUserDisplayName,
    isAgent,
  } = useContext(AuthContext);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      navigate("/", { replace: true });
    }
  }, [logout, navigate]);

  const formatMobileForDisplay = (mobile) => {
    if (!mobile) return "User";
    const cleaned = mobile.replace(/\D/g, "");
    if (cleaned.length >= 10) {
      return `+91 ${cleaned.slice(-10, -5)}***${cleaned.slice(-2)}`;
    }
    return mobile;
  };

  const getDisplayName = () => {
    if (!isLoggedIn || !user) return "Login or Signup";
    const displayName = getUserDisplayName();
    if (displayName && displayName !== "User" && displayName !== "Agent") {
      return displayName;
    }
    return formatMobileForDisplay(user.mobile);
  };

  const handleDashboardNavigation = () => {
    const currentUserIsAgent =
      user &&
      (user.role === "agent" ||
        user.userType === "agent" ||
        user.type === "agent" ||
        userType === "agent");
    navigate(currentUserIsAgent ? "/dashboard?tab=dashboard" : "/dashboard?tab=profile");
  };

  // Logged out: a single click opens the login/signup popup directly —
  // no page navigation, no extra dropdown step in the way.
  if (!isLoggedIn) {
    return (
      <>
        <button
          onClick={() => setShowLogin(true)}
          className="flex items-center gap-2 rounded-xl bg-ink-900 text-white px-4 py-2.5 text-sm font-semibold hover:bg-ink-800 transition shrink-0"
        >
          <BsPersonCircle size={17} />
          <span className="hidden sm:inline">Login / Signup</span>
          <span className="sm:hidden">Login</span>
        </button>

        <Modal show={showLogin} onHide={() => setShowLogin(false)} centered size="sm">
          <Modal.Header closeButton className="border-0 pb-0" />
          <Modal.Body className="pt-0">
            <EmailLogin isModal onSuccess={() => setShowLogin(false)} />
            <div className="text-center mt-2">
              <button
                onClick={() => {
                  setShowLogin(false);
                  navigate("/login/agent");
                }}
                className="text-xs text-ink-400 hover:text-brand-600 transition underline-offset-2 hover:underline"
              >
                Login as a travel agent instead
              </button>
            </div>
          </Modal.Body>
        </Modal>
      </>
    );
  }

  // Logged in: keep the small account dropdown (dashboard / logout).
  return (
    <Dropdown align="end">
      <Dropdown.Toggle
        as="div"
        role="button"
        tabIndex="0"
        className="flex items-center gap-2 rounded-xl border border-ink-200 px-3.5 py-2 text-sm font-semibold text-ink-800 hover:bg-ink-50 transition cursor-pointer"
      >
        {isAgent() ? <BsPersonBadge size={17} /> : <BsPersonCircle size={17} />}
        <span className="max-w-[120px] truncate">{getDisplayName()}</span>
      </Dropdown.Toggle>

      <Dropdown.Menu>
        <Dropdown.Item onClick={handleDashboardNavigation}>
          <i className="fas fa-tachometer-alt me-2"></i>
          Dashboard
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item onClick={handleLogout} className="text-danger" style={{ cursor: "pointer" }}>
          <i className="fas fa-sign-out-alt me-2"></i>
          Logout
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default Login;
